/* eslint-disable no-param-reassign */
import React, { useEffect, useReducer } from 'react';

import clsx from 'clsx';
import { isEmpty } from 'lodash';
import KeyStore from 'utils/KeyStore';
import ArrayUtils from 'lib/ArrayUtils';
import update from 'immutability-helper';
import ModalUtils from 'utils/ModalUtils';
import Table from 'components/widgets/Table';
import { FetchPolicy } from 'utils/enum/Core';
import Filter from 'components/widgets/Filter';
import Permission from 'utils/enum/Permissions';
import Loading from 'components/widgets/Loading';
import { makeStyles, Button } from '@material-ui/core';
import InputSearch from 'components/widgets/InputSearch';
import { useMutation, useLazyQuery } from '@apollo/client';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import FormsMapperQuery from 'services/graphQL/query/setting/FormsMapperQuery';
import { AddIcon, EditOutlinedIcon, DeleteOutlineIcon } from 'components/icons';
import CustomFieldsDialog from 'components/modules/settings/form/CustomFieldsDialog';
import FormsMapperMutation from 'services/graphQL/mutate/setting/FormsMapperMutation';

const useStyles = makeStyles((theme) => ({
    tableHeight: {
        display: 'contents',
    },
    boxContent: {
        overflow: 'hidden',
    },
    root: {
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        flexDirection: 'column',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    buttonSpacing: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-end',
        '& > *': {
            margin: theme.spacing(0.3),
        },
    },
    hidden: {
        display: 'none',
    },
    actionButtonSuccess: {
        color: theme.palette.text.outerSpace,
    },
    actionButtonError: {
        color: theme.palette.error.main,
    },
    filterContainer: {
        height: '20px',
        display: 'flex',
        cursor: 'pointer',
        alignItems: 'center',
        justifyContent: 'center',
        '& > div': {
            left: '5px',
            width: '15%',
            display: 'flex',
            paddingTop: '4px',
            textAlign: 'left',
            paddingBottom: '4px',
            position: 'absolute',
        },
    },
    loadingIndicator: {
        flex: 1,
        height: '100%',
    },
    searchBox: {
        width: 'auto',
        marginLeft: 20,
    },
}));

const ACTION_TYPES = {
    ON_SEARCH: 'onSearch',
    INIT_STATE: 'initState',
    APPLY_FILTER: 'applyFilter',
    TOGGLE_DELETE_DIALOG: 'toggleDeleteDialog',
    TOGGLE_ADD_EDIT_DIALOG: 'toggleAddEditDialog',
};

const reducer = (state, action) => {
    const {
        type,
        value,
        accessor,
    } = action;
    switch (type) {
    case ACTION_TYPES.INIT_STATE:
        return update(state, {
            items: { $set: value },
            loadTable: { $set: true },
            selectedRecord: { $set: null },
            openAddEditDialog: { $set: false },
            openDeleteDialoog: { $set: false },
        });
    case ACTION_TYPES.ON_SEARCH:
        return update(state, {
            search: { $set: value },
            loadTable: { $set: false },
        });
    case ACTION_TYPES.TOGGLE_DELETE_DIALOG:
        const { openDeleteDialog } = state;
        return update(state, {
            selectedRecord: { $set: value },
            openDeleteDialog: { $set: !openDeleteDialog },
        });
    case ACTION_TYPES.TOGGLE_ADD_EDIT_DIALOG:
        const { openAddEditDialog } = state;
        return update(state, {
            selectedRecord: { $set: value },
            openAddEditDialog: { $set: !openAddEditDialog },
        });
    case ACTION_TYPES.APPLY_FILTER:
        const { filtersApplied } = state;
        const filtered = [...filtersApplied];
        let insertNewFilter = 1;

        if (filtered.length) {
            filtered.forEach((filter, i) => {
                if (filter.id === accessor) {
                    if (value === '' || !value.length) filtered.splice(i, 1);
                    else filter.value = value;
                    insertNewFilter = 0;
                }
            });
        }

        if (insertNewFilter && value.length) {
            filtered.push({ id: accessor, value });
        }
        return update(state, {
            filtersApplied: { $set: filtered },
        });
    default: return state;
    }
};

const initState = {
    items: [],
    search: '',
    loadTable: false,
    filtersApplied: [],
    selectedRecord: null,
    openDeleteDialog: false,
    openAddEditDialog: false,
};

const FormFields = () => {
    const classes = useStyles();
    const keyStore = new KeyStore();
    const [state, dispatch] = useReducer(reducer, initState);

    const {
        openAddEditDialog, loadTable, filtersApplied,
        items, openDeleteDialog, selectedRecord, search,
    } = state;

    const [getData, {
        loading, data, error, refetch,
    }] = useLazyQuery(FormsMapperQuery.GET_FORMS_MAPPED_FIELDS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    useEffect(() => {
        getData({
            variables: {
                search,
            },
        });
    }, [search, getData]);

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
            return;
        }

        if (!loading && !isEmpty(data)) {
            const { getFormsMappedFields } = data;
            const sortedList = ArrayUtils.sortByObjectField(getFormsMappedFields, 'fieldsTableMappingId');
            dispatch({
                value: sortedList,
                type: ACTION_TYPES.INIT_STATE,
            });
        }
    }, [data, loading, error]);

    const toggleDeleteDialog = ({ record }) => dispatch({
        value: record,
        type: ACTION_TYPES.TOGGLE_DELETE_DIALOG,
    });

    const [removeFormsMappedField, { loading: removingFieldMapped }] = useMutation(FormsMapperMutation.REMOVE_FORMS_MAPPED_FIELD, {
        onCompleted: (mutationData) => {
            if (mutationData && mutationData.removeFormsMappedField) {
                ModalUtils.successMessage(null, 'Removed Successfully');
                refetch();
            }
        },
        onError: (mutationError) => {
            ModalUtils.errorMessage(null, mutationError);
        },
    });

    const toggleAddEdit = ({ record = null, saving = false }) => {
        dispatch({
            value: record,
            type: ACTION_TYPES.TOGGLE_ADD_EDIT_DIALOG,
        });
        if (saving) refetch();
    };

    const onSearch = (value) => {
        dispatch({
            value,
            type: ACTION_TYPES.ON_SEARCH,
        });
    };

    const getFilterValues = (values, columnId) => {
        if (values?.length > 0) {
            const mappedRecords = [
                ...new Map(values
                    .map((a) => ({ value: a[columnId], label: (String(a[columnId]) || '(Blanks)') }))
                    .map((item) => [item.label, item])).values(),
            ];
            const sortedResult = ArrayUtils.sortByObjectField(mappedRecords, 'value');
            return sortedResult;
        }
        return [];
    };

    const onFilteredChangeCustom = (value, accessor) => {
        dispatch({
            value,
            accessor,
            type: ACTION_TYPES.APPLY_FILTER,
        });
    };

    const resetFilters = (columnId) => {
        onFilteredChangeCustom([], columnId);
    };

    const getColumns = () => [{
        width: 120,
        Header: 'ID',
        id: 'fieldsTableMappingId',
        accessor: 'fieldsTableMappingId',
        className: 'd-flex-justify-center-align-center',
    }, {
        width: 300,
        id: 'name',
        Header: 'Label',
        accessor: 'name',
        className: 'd-flex-justify-center-align-center',
    }, {
        Header: 'Value',
        id: 'columnName',
        accessor: 'columnName',
        className: 'd-flex-justify-center-align-center',
    }, {
        id: 'actions',
        width: 'auto',
        Header: 'Actions',
        headerClassName: clsx(classes.hidden),
        className: clsx(classes.hidden, 'actionColumnTarget'),
        Cell: (cellData) => {
            const { original } = cellData;

            return (
                <div className={classes.buttonSpacing}>
                    {keyStore.hasPermission(Permission.FORMS_SETTINGS_FIELDS_WRITE) && !original.isCore && (
                        <>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => toggleAddEdit({ record: original })}
                                startIcon={<EditOutlinedIcon className={classes.actionButtonSuccess} />}
                            >
                                Edit
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => toggleDeleteDialog({ record: original })}
                                startIcon={<DeleteOutlineIcon className={classes.actionButtonError} />}
                            >
                                Delete
                            </Button>
                        </>
                    )}
                </div>
            );
        },
    }];

    const loadColumns = () => {
        const columns = getColumns();
        columns.forEach((column) => {
            column.Header = (
                <div
                    className={classes.filterContainer}
                >
                    {column.Header}
                    <div
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <Filter
                            showTooltip
                            showIconOnly
                            useInternalSearch
                            maxWidthLabel={200}
                            records={getFilterValues(items, column.id)}
                            onClearFilter={() => resetFilters(column.id)}
                            applyFilter={(record) => onFilteredChangeCustom(
                                record.map((item) => (item.label === '(Blanks)' ? '' : item.value)), column.id,
                            )}
                        />
                    </div>
                </div>
            );
        });
        return columns;
    };

    return (
        <>
            <div className={classes.root}>
                <div className={classes.header}>
                    {keyStore.hasPermission(Permission.FORMS_SETTINGS_FIELDS_WRITE) && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={toggleAddEdit}
                        >
                            New Field Mapping
                        </Button>
                    )}
                    <InputSearch
                        size="sm"
                        onSearch={onSearch}
                        initialSearch={search || ''}
                        customClasses={classes.searchBox}
                    />
                </div>
                <div className={classes.tableHeight}>
                    {!loadTable && (
                        <div className={classes.loadingIndicator}>
                            <Loading />
                        </div>
                    )}
                    {loadTable && (
                        <Table
                            data={items}
                            load={loading}
                            columns={loadColumns()}
                            filtered={filtersApplied}
                            totalRecords={items.count}
                            className="-highlight actionsInLine payrollTable"
                            defaultFilterMethod={(localFilter, row) => {
                                const id = localFilter.pivotId || localFilter.id;
                                if (typeof localFilter.value === 'object') {
                                    return row[id] !== undefined
                                        ? localFilter.value.indexOf(row[id]) > -1
                                        : true;
                                }
                                return row[id] !== undefined
                                    ? String(row[id]).indexOf(localFilter.value) > -1
                                    : true;
                            }}
                        />
                    )}
                </div>
            </div>
            <ConfirmDialog
                titlePrimary="Yes"
                variant="outlined"
                open={openDeleteDialog}
                titleSecondary="Cancel"
                title="Confirm remove field mapping"
                disablePrimaryButton={removingFieldMapped}
                disableSecondaryButton={removingFieldMapped}
                onClose={() => toggleDeleteDialog({ record: null })}
                onClickSecondary={() => toggleDeleteDialog({ record: null })}
                description="Are you sure you want to remove this field mapping?"
                onClickPrimary={() => {
                    toggleDeleteDialog({ record: null });
                    removeFormsMappedField({ variables: { id: selectedRecord.fieldsTableMappingId } });
                }}
            />
            {openAddEditDialog && (
                <CustomFieldsDialog onClose={toggleAddEdit} record={selectedRecord} />
            )}
        </>
    );
};

export default FormFields;
